/**
 * getTimeZoneName
 * --------------------------------------
 * Returns the time zone name (usually an abbreviation like CST / CDT / EST / PST)
 * or the full name depending on the requested style. Prefers the built‑in
 * Intl.DateTimeFormat API and falls back to moment-timezone formatting when
 * necessary.
 *
 * Examples:
 *   getTimeZoneName(new Date())                 => "CDT" (depending on current TZ & DST)
 *   getTimeZoneName("2025-09-19T15:00:00Z")    => "UTC"
 *   getTimeZoneName(date, "America/Chicago")   => "CDT"
 *   getTimeZoneName(date, "America/Chicago", { type: "long" }) => "Central Daylight Time"
 *
 * @param dateInput  A Date object, ISO string, or epoch milliseconds.
 * @param timeZone   (optional) IANA time zone name (e.g. "America/Chicago"). If omitted, local TZ is used.
 * @param options    (optional) { type: 'short' | 'long' } -> default 'short'.
 * @returns The time zone name / abbreviation, or empty string if it cannot be determined.
 */
import moment from "moment-timezone";

export interface GetTimeZoneNameOptions {
  /** 'short' gives abbreviations (e.g. CST), 'long' gives full names (e.g. Central Standard Time) */
  type?: "short" | "long";
}

export function getTimeZoneName(
  dateInput: Date | string | number,
  timeZone?: string | null,
  options: GetTimeZoneNameOptions = {}
): string {
  if (dateInput == null) return "";

  const { type = "short" } = options;
  let date: Date;
  try {
    date =
      dateInput instanceof Date
        ? dateInput
        : new Date(
            typeof dateInput === "number" ? dateInput : Date.parse(dateInput)
          );
    if (isNaN(date.getTime())) return "";
  } catch {
    return "";
  }

  // Primary attempt: Intl API
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone ?? undefined,
      timeZoneName: type,
    });
    const parts = dtf.formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    if (tzPart?.value) {
      // Some environments return GMT-XX offsets for 'short'. Accept as-is.
      return tzPart.value;
    }
  } catch {
    // Ignore and fall back to moment
  }

  // Fallback: moment-timezone (abbreviation only)
  try {
    if (timeZone) {
      const m = moment.tz(date, timeZone);
      if (m && m.isValid()) {
        return type === "long" ? m.tz() || "" : m.format("z") || "";
      }
    } else {
      const m = moment(date);
      if (m && m.isValid()) {
        return type === "long" ? m.tz() || "" : m.format("z") || "";
      }
    }
  } catch {
    // Swallow
  }

  return "";
}

export default getTimeZoneName;
